import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { CheckCircle, ShieldCheck, Plus, X, Warning, Buildings, Calendar } from "@phosphor-icons/react";
import PhotoUploader from "@/components/PhotoUploader";
import DocumentUploader from "@/components/DocumentUploader";
import WeeklyAvailabilityEditor from "@/components/WeeklyAvailabilityEditor";

// Cleaning verticals only (Feb 2026 pivot). Mirrors backend TASK_CATEGORIES.
const SERVICE_CATEGORIES = [
    "One-time Cleaning",
    "Weekly Cleaning",
    "Bi-weekly Cleaning",
    "Monthly Cleaning",
    "Move-in / Move-out Deep Clean",
    "Exterior Windows",
    "Garage Cleaning",
    "Oven Cleaning (interior)",
    "Refrigerator Cleaning (interior)",
    "Laundry & Folding",
];
const DEFAULT_CITIES = ["Orange", "Kissimmee", "Seminole", "Lake Mary"];

export default function PartnerProfilePage() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [quality, setQuality] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [newReview, setNewReview] = useState({ client_name: "", text: "", rating: 5 });
    const [cityInput, setCityInput] = useState("");

    const [form, setForm] = useState({
        years_in_business: "",
        insurance_status: "no",
        insurance_carrier: "",
        insurance_pdf_path: "",
        bio: "",
        service_categories: [],
        hours_of_operation: "",
        service_cities: [],
        state_license_number: "",
        state_license_pdf_path: "",
        county_license_number: "",
        county_license_pdf_path: "",
        local_license_number: "",
        local_license_pdf_path: "",
        project_photos: [],
        website: "",
        phone: "",
        address: "",
        reviews: [],
        contractor_w9_pdf_path: "",
        weekly_availability: {},
    });

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await http.get("/partner/profile");
                if (cancel) return;
                setProfile(data.application);
                setQuality(data.quality);
                const a = data.application || {};
                setForm({
                    years_in_business: a.years_in_business ?? "",
                    insurance_status: a.insurance_status || "no",
                    insurance_carrier: a.insurance_carrier || "",
                    insurance_pdf_path: a.insurance_pdf_path || "",
                    bio: a.bio || "",
                    service_categories: a.service_categories || [],
                    hours_of_operation: a.hours_of_operation || "",
                    service_cities: a.service_cities && a.service_cities.length ? a.service_cities : [...DEFAULT_CITIES],
                    state_license_number: a.state_license_number || "",
                    state_license_pdf_path: a.state_license_pdf_path || "",
                    county_license_number: a.county_license_number || "",
                    county_license_pdf_path: a.county_license_pdf_path || "",
                    local_license_number: a.local_license_number || "",
                    local_license_pdf_path: a.local_license_pdf_path || "",
                    project_photos: a.project_photos || [],
                    website: a.website || "",
                    phone: a.phone || "",
                    address: a.address || "",
                    reviews: a.reviews || [],
                    contractor_w9_pdf_path: a.contractor_w9_pdf_path || "",
                    weekly_availability: a.weekly_availability || {},
                });
            } catch (e) {
                if (!cancel) setError(formatApiError(e));
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, []);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    function toggleCategory(c) {
        set("service_categories",
            form.service_categories.includes(c)
                ? form.service_categories.filter((x) => x !== c)
                : [...form.service_categories, c]);
    }

    function addCity() {
        const c = cityInput.trim();
        if (!c || form.service_cities.includes(c)) { setCityInput(""); return; }
        set("service_cities", [...form.service_cities, c]);
        setCityInput("");
    }
    function removeCity(c) {
        set("service_cities", form.service_cities.filter((x) => x !== c));
    }

    function addReview() {
        const r = { ...newReview, client_name: newReview.client_name.trim(), text: newReview.text.trim() };
        if (!r.client_name || !r.text) { setError("Review needs a name and text."); return; }
        set("reviews", [...form.reviews, r]);
        setNewReview({ client_name: "", text: "", rating: 5 });
        setError("");
    }
    function removeReview(i) {
        set("reviews", form.reviews.filter((_, idx) => idx !== i));
    }

    async function save() {
        setSaving(true); setError(""); setSuccess(false);
        try {
            const body = { ...form };
            body.years_in_business = body.years_in_business === "" ? null : parseInt(body.years_in_business, 10);
            const { data } = await http.post("/partner/profile", body);
            setProfile(data.application);
            setQuality(data.quality);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) { setError(formatApiError(e)); }
        finally { setSaving(false); }
    }

    if (loading) return <div className="max-w-4xl mx-auto px-6 py-16 text-slate-500">Loading…</div>;
    if (!profile) return <div className="max-w-4xl mx-auto px-6 py-16 text-slate-500">No application found.</div>;

    return (
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-12" data-testid="partner-profile-page">
            <div className="mb-10">
                <Link to="/partner" className="text-xs text-slate-500 hover:text-gold uppercase tracking-widest">← Back to dashboard</Link>
                <div className="label-eyebrow mt-4 mb-3">/ Partner profile</div>
                <h1 className="font-serif text-4xl sm:text-5xl">Build your <span className="italic gold-text">profile</span></h1>
                <p className="text-slate-300 mt-3 max-w-2xl text-sm leading-relaxed">
                    A complete profile earns a higher quality score, which means you'll show up first in homeowner matches.
                </p>
            </div>

            {/* Quality score gauge */}
            {quality && (
                <div data-testid="quality-card" className="mb-10 rounded-sm border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950 p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="label-eyebrow text-gold">/ Quality score</div>
                            <div className="flex items-baseline gap-2 mt-2">
                                <span data-testid="quality-score-value" className="font-serif text-6xl text-gold">{quality.score}</span>
                                <span className="text-2xl text-slate-500">/ 100</span>
                            </div>
                        </div>
                        <div className="flex-1 min-w-[260px]">
                            <ScoreBar label="Reviews" {...quality.breakdown.reviews} />
                            <ScoreBar label="Photos" {...quality.breakdown.photos} />
                            <ScoreBar label="Licensing" {...quality.breakdown.licensing} />
                            <ScoreBar label="Experience" {...quality.breakdown.experience} />
                        </div>
                    </div>
                    {quality.tips && quality.tips.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-white/5">
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">How to boost your score</div>
                            <ul className="space-y-1 text-sm text-slate-300">
                                {quality.tips.map((t, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <Plus size={12} weight="bold" className="text-gold mt-1.5 shrink-0" /> {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {success && (
                <div data-testid="profile-saved" className="mb-6 rounded-sm border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center gap-2 text-emerald-300">
                    <CheckCircle size={18} weight="fill" /> Profile saved.
                </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-8">
                <Section title="About your business" icon={<Buildings size={18} weight="duotone" />}>
                    <Field label="Bio (max 1200 chars)" textarea testid="prof-bio" value={form.bio} onChange={(v) => set("bio", v)} />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Years in business" type="number" testid="prof-years" value={form.years_in_business}
                            onChange={(v) => set("years_in_business", v)} />
                        <Field label="Website" testid="prof-website" value={form.website} onChange={(v) => set("website", v)} placeholder="https://" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Phone" testid="prof-phone" value={form.phone} onChange={(v) => set("phone", v)} />
                        <Field label="Address" testid="prof-address" value={form.address} onChange={(v) => set("address", v)} />
                    </div>
                    <Field label="Hours of operation" testid="prof-hours" value={form.hours_of_operation}
                        onChange={(v) => set("hours_of_operation", v)} placeholder="Mon-Fri 8am-6pm, Sat 9am-3pm" />
                </Section>

                <Section title="Services you offer" icon={<CheckCircle size={18} weight="duotone" />}>
                    <div className="flex flex-wrap gap-2">
                        {SERVICE_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                type="button"
                                data-testid={`prof-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => toggleCategory(c)}
                                className={`px-3 py-1.5 text-xs rounded-full border transition ${
                                    form.service_categories.includes(c)
                                        ? "border-gold bg-gold/10 text-gold"
                                        : "border-white/10 text-slate-300 hover:border-white/25"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </Section>

                <Section title="Weekly availability" icon={<Calendar size={18} weight="duotone" />}>
                    <WeeklyAvailabilityEditor
                        value={form.weekly_availability}
                        onChange={(v) => set("weekly_availability", v)}
                    />
                </Section>

                <Section title="Service cities (Central Florida)" icon={<Buildings size={18} weight="duotone" />}>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {form.service_cities.map((c) => (
                            <span key={c} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-gold/10 text-gold border border-gold/30">
                                {c}
                                <button type="button" data-testid={`remove-city-${c}`} onClick={() => removeCity(c)} className="hover:text-white"><X size={10} /></button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            data-testid="city-input"
                            value={cityInput}
                            onChange={(e) => setCityInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(); } }}
                            placeholder="Add a city (e.g. Winter Park)"
                            className="flex-1 bg-slate-950 border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold"
                        />
                        <button type="button" data-testid="add-city" onClick={addCity} className="btn-ghost text-sm">Add</button>
                    </div>
                </Section>

                <Section title="Insurance & licensing" icon={<ShieldCheck size={18} weight="duotone" />}>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-widest text-slate-400">Currently insured?</label>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                                {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }].map((o) => (
                                    <button key={o.v} type="button" data-testid={`prof-ins-${o.v}`} onClick={() => set("insurance_status", o.v)}
                                        className={`px-3 py-2 text-sm border rounded-sm transition ${form.insurance_status === o.v ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-slate-300 hover:border-white/25"}`}>
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <Field label="Insurance carrier" testid="prof-carrier" value={form.insurance_carrier}
                            onChange={(v) => set("insurance_carrier", v)} placeholder="e.g. State Farm" />
                    </div>
                    <DocumentUploader
                        label="Certificate of insurance (PDF or image)"
                        testidPrefix="prof-ins-doc"
                        purpose="insurance"
                        value={form.insurance_pdf_path}
                        onChange={(v) => set("insurance_pdf_path", v)}
                    />

                    <div className="pt-2 border-t border-white/5" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="State license #" testid="prof-state-lic" value={form.state_license_number} onChange={(v) => set("state_license_number", v)} />
                        <DocumentUploader
                            label="State license document"
                            testidPrefix="prof-state-lic-doc"
                            purpose="license_state"
                            value={form.state_license_pdf_path}
                            onChange={(v) => set("state_license_pdf_path", v)}
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="County license #" testid="prof-county-lic" value={form.county_license_number} onChange={(v) => set("county_license_number", v)} />
                        <DocumentUploader
                            label="County license document"
                            testidPrefix="prof-county-lic-doc"
                            purpose="license_county"
                            value={form.county_license_pdf_path}
                            onChange={(v) => set("county_license_pdf_path", v)}
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Local / trade license #" testid="prof-local-lic" value={form.local_license_number} onChange={(v) => set("local_license_number", v)} />
                        <DocumentUploader
                            label="Local license document"
                            testidPrefix="prof-local-lic-doc"
                            purpose="license_local"
                            value={form.local_license_pdf_path}
                            onChange={(v) => set("local_license_pdf_path", v)}
                        />
                    </div>
                    <p className="text-xs text-slate-500">All uploaded documents are stored privately. Only Butler Room admin can view them.</p>
                </Section>

                <Section title="IRS Form W-9" icon={<ShieldCheck size={18} weight="duotone" />}>
                    <p className="text-sm text-slate-300 leading-relaxed">
                        Before we can pay you out, IRS rules require a completed W-9. Download the latest
                        official IRS form, fill it out, then upload the completed PDF here.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-3">
                        <a
                            href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="w9-irs-download"
                            className="btn-ghost text-sm inline-flex items-center gap-2"
                        >
                            Download IRS Form W-9 (PDF) →
                        </a>
                    </div>
                    <div className="mt-3">
                        <DocumentUploader
                            label="Upload your completed and signed W-9"
                            testidPrefix="prof-w9-doc"
                            purpose="w9"
                            value={form.contractor_w9_pdf_path}
                            onChange={(v) => set("contractor_w9_pdf_path", v)}
                        />
                    </div>
                </Section>

                <Section title="Project photo gallery">
                    <p className="text-sm text-slate-400 mb-3">Real project photos only — no stock images. Up to 10.</p>
                    <PhotoUploader value={form.project_photos} onChange={(v) => set("project_photos", v)} maxPhotos={10} />
                </Section>

                <Section title="Customer reviews">
                    <div className="space-y-3">
                        {form.reviews.map((r, i) => (
                            <div key={i} data-testid={`review-${i}`} className="rounded-sm border border-white/5 bg-slate-950 p-3 flex items-start justify-between gap-3">
                                <div className="flex-1 text-sm">
                                    <div className="text-gold flex items-center gap-2">
                                        <span>{r.client_name}</span>
                                        {r.rating && <span className="text-xs text-slate-500">· {r.rating}/5 ★</span>}
                                    </div>
                                    <div className="text-slate-300 mt-1">{r.text}</div>
                                </div>
                                <button type="button" data-testid={`remove-review-${i}`} onClick={() => removeReview(i)} className="text-slate-500 hover:text-red-400"><X size={14} /></button>
                            </div>
                        ))}
                        <div className="rounded-sm border border-dashed border-white/15 p-3 space-y-2">
                            <div className="grid sm:grid-cols-3 gap-2">
                                <input data-testid="rev-name" value={newReview.client_name} onChange={(e) => setNewReview({ ...newReview, client_name: e.target.value })}
                                    placeholder="Client name" className="bg-slate-950 border border-white/10 rounded-sm px-3 py-2 text-sm sm:col-span-2 focus:outline-none focus:border-gold" />
                                <select data-testid="rev-rating" value={newReview.rating} onChange={(e) => setNewReview({ ...newReview, rating: parseInt(e.target.value, 10) })}
                                    className="bg-slate-950 border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold">
                                    {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} ★</option>)}
                                </select>
                            </div>
                            <textarea data-testid="rev-text" value={newReview.text} onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                                placeholder="Short quote from the client…" rows={2}
                                className="w-full bg-slate-950 border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold" />
                            <button type="button" data-testid="add-review" onClick={addReview} className="btn-ghost text-xs">+ Add review</button>
                        </div>
                    </div>
                </Section>

                {error && <p data-testid="profile-error" className="text-red-400 text-sm flex items-center gap-2"><Warning size={16} weight="fill" /> {error}</p>}

                <div className="flex flex-wrap gap-3 pt-2 sticky bottom-4 bg-[#0B1220]/80 backdrop-blur p-3 rounded-sm border border-white/5">
                    <button type="submit" data-testid="profile-save" disabled={saving} className="btn-gold disabled:opacity-50">
                        {saving ? "Saving…" : "Save profile"}
                    </button>
                    <button type="button" onClick={() => navigate("/partner")} className="btn-ghost">Done</button>
                </div>
            </form>
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <section className="bg-slate-900/60 border border-white/10 rounded-sm p-6 space-y-4">
            <h2 className="font-serif text-xl text-white flex items-center gap-2">{icon}{title}</h2>
            {children}
        </section>
    );
}

function Field({ label, testid, value, onChange, type = "text", textarea = false, placeholder = "" }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
            {textarea ? (
                <textarea
                    data-testid={testid}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={4} placeholder={placeholder}
                    className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
                />
            ) : (
                <input
                    data-testid={testid}
                    type={type} value={value === null || value === undefined ? "" : value}
                    onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                    className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
                />
            )}
        </div>
    );
}

function ScoreBar({ label, earned, max }) {
    const pct = Math.round((earned / max) * 100);
    return (
        <div className="mb-2">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
                <span>{label}</span><span className="font-mono">{earned} / {max}</span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-gold to-amber-400 transition-all" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
